import axios, { AxiosError } from 'axios';
import { Store } from '@reduxjs/toolkit';
import { logout } from '../store/slices/OrganizationSlice';

const baseURL = process.env.REACT_APP_BASE_URL;


let store: Store | null = null;

export const injectStore = (_store: Store) => {
  store = _store;
};

// Create axios instance
const axiosInstance = axios.create({
  baseURL,
});

// Response interceptor to handle 401 errors
axiosInstance.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Token expired or unauthorized
      console.log('Token expired or unauthorized, logging out...');
      
      // Clear localStorage
      localStorage.removeItem('accessToken');
      
      // Clear Redux state if store is available
      if (store) {
        store.dispatch(logout());
      }
      
      // Navigate to login - we'll use window.location to ensure it works everywhere
      window.location.href = '/';
    }
    
    // Wrap non-Error rejection reasons into an Error object
    const rejectionReason = error instanceof Error ? error : new Error(String(error));
    return Promise.reject(rejectionReason);
  }
);

export const setRequestHeaders = (requestHeaders: any = {},contentType="application/json") => {
  const token =  localStorage.getItem('accessToken');
  //  const token = ";
  const headers = {
    'Content-Type': contentType,
    'authorization':`Bearer ${token}`,
    ...requestHeaders,
  };
  // If the content type is multipart/form-data, let Axios handle the content type
  if (contentType === 'multipart/form-data') {
    delete headers['Content-Type']; // Remove the Content-Type header for FormData
  } else {
    headers['Content-Type'] = contentType;
  }
  return headers;
};


const http = {

  get: async (url: string) => {
    try {
      let requestHeaders: any = setRequestHeaders();
      const response = await axiosInstance.get(url, { headers: requestHeaders });
      return response.data;
    } catch (error) {
      console.error('Error making GET request:', error);
      throw error;
    }
  },

  post: async (url: string, data: any, contentType="application/json") => {
    try {
      let requestHeaders: any = setRequestHeaders({}, contentType);
      const response = await axiosInstance.post(url, data, { headers: requestHeaders });
      return response.data;
    } catch (error) {
      console.error('Error making POST request:', error);
      throw error;
    }
  },

  put: async (url: string, data: any, contentType="application/json") => {
    try {
      let requestHeaders: any = setRequestHeaders({}, contentType);
      const response = await axiosInstance.put(url, data, { headers: requestHeaders });
      return response.data;
    } catch (error) {
      console.error('Error making PUT request:', error);
      throw error;
    }
  },

  patch: async (url: string, data: any, contentType="application/json") => {
    try {
      let requestHeaders: any = setRequestHeaders({}, contentType);
      const response = await axiosInstance.patch(url, data, { headers: requestHeaders });
      return response.data;
    } catch (error) {
      console.error('Error making PATCH request:', error);
      throw error;
    }
  },

  delete: async (url: string) => {
    try {
      let requestHeaders = setRequestHeaders();
      const response = await axiosInstance.delete(url, { headers: requestHeaders });
      return response.data;
    } catch (error) {
      console.error('Error making DELETE request:', error);
      throw error;
    }
  },
};

// Export the axios instance for direct use if needed
export { axiosInstance };

export default http;
